import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLanguage } from "../../../redux/features/language/languageSlice";
import { RootState } from "../../../redux/store";
import { LanguageCode } from "../../../redux/features/language/languageTypes";
import { languageSwitcherOptions } from "../../../localization/supportedLanguages";
import { buildAppLink } from "../../../utils/landingRedirect";

const Navbar: React.FC = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state: RootState) => state.language.current);

  const [scrolled, setScrolled] = React.useState(false);
  const [featuresOpen, setFeaturesOpen] = React.useState(false);
  const [toolsOpen, setToolsOpen] = React.useState(false);
  const [languageOpen, setLanguageOpen] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleAnchorClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    targetId: string,
  ) => {
    event.preventDefault();

    const target = document.getElementById(targetId);
    if (!target) return;

    const navHeight =
      document.querySelector("[data-landing-nav]")?.getBoundingClientRect()
        .height ?? 0;
    const top = target.getBoundingClientRect().top + window.scrollY - navHeight;

    window.history.pushState(null, "", `#${targetId}`);
    window.scrollTo({
      top: Math.max(0, top),
      behavior: "smooth",
    });
  };

  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setMobileMenuOpen(false);
    setLanguageOpen(false);
    setFeaturesOpen(false);
    setToolsOpen(false);

    if (location.pathname !== "/" || location.hash) {
      navigate("/");
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleLanguageChange = (lang: LanguageCode) => {
    dispatch(setLanguage(lang));
    i18nInstance.changeLanguage(lang);
    setLanguageOpen(false);
  };

  // Features dropdown items - linking to tool pages for now
  const featuresItems = [
    { labelKey: "modern.navbar.dropdown.aiTranscription", href: "/tools/audio-transcriber", icon: "lucide:mic" },
    { labelKey: "modern.navbar.dropdown.pdfSummarizer", href: "/tools/pdf-summarizer", icon: "lucide:file-text" },
    { labelKey: "modern.navbar.dropdown.youtubeSummarizer", href: "/tools/youtube-summarizer", icon: "lucide:youtube" },
    { labelKey: "modern.navbar.dropdown.flashcardsQuizzes", href: "/tools/flashcard-maker", icon: "lucide:book-open" },
    { labelKey: "modern.navbar.dropdown.meetingNotes", href: "/tools/meeting-notes", icon: "lucide:users" },
    { labelKey: "modern.navbar.dropdown.lectureNotes", href: "/tools/lecture-notes", icon: "lucide:graduation-cap" },
  ];

  // Free AI Tools dropdown items (for SEO)
  const aiToolsItems = [
    { labelKey: "modern.navbar.dropdown.aiAudioTranscriber", href: "/tools/audio-transcriber", icon: "lucide:mic" },
    { labelKey: "modern.navbar.dropdown.youtubeVideoSummarizer", href: "/tools/youtube-summarizer", icon: "lucide:youtube" },
    { labelKey: "modern.navbar.dropdown.pdfSummaryGenerator", href: "/tools/pdf-summarizer", icon: "lucide:file-text" },
    { labelKey: "modern.navbar.dropdown.aiFlashcardMaker", href: "/tools/flashcard-maker", icon: "lucide:layers" },
    { labelKey: "modern.navbar.dropdown.aiQuizGenerator", href: "/tools/quiz-generator", icon: "lucide:help-circle" },
    { labelKey: "modern.navbar.dropdown.meetingNotesGenerator", href: "/tools/meeting-notes", icon: "lucide:users" },
    { labelKey: "modern.navbar.dropdown.lectureNotesAi", href: "/tools/lecture-notes", icon: "lucide:graduation-cap" },
    { labelKey: "modern.navbar.dropdown.exploreAllTools", href: "/tools", icon: "lucide:grid" },
  ];

  // Handle scroll effect
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrolled(scrollTop > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const DropdownMenu = ({
    items,
    isOpen,
    onItemClick
  }: {
    items: typeof featuresItems;
    isOpen: boolean;
    onItemClick?: () => void;
  }) => (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Invisible bridge to prevent dropdown from closing */}
          <div className="absolute top-full left-0 w-full h-2" />
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 pt-2 w-56 z-50"
          >
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 py-2">
              {items.map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  onClick={onItemClick}
                  className="flex items-center gap-3 px-4 py-2.5 text-gray-600 hover:bg-gray-50 hover:text-cornflowerblue transition-colors no-underline"
                >
                  <Icon icon={item.icon} className="text-lg text-gray-400" />
                  <span className="text-sm font-medium">{t(item.labelKey)}</span>
                </Link>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 py-4 px-4 md:px-6 transition-all duration-300 bg-white ${
        scrolled ? "shadow-lg" : "shadow-md"
      }`}
      data-landing-nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="container mx-auto flex items-center justify-between relative z-10">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <Link to="/" onClick={handleLogoClick} className="flex items-center gap-1 no-underline">
            <img
              className="w-[41px] relative h-[41px] object-cover"
              alt="Logo"
              src="/logo/logo.png"
            />
            <span className="whitespace-nowrap text-xl font-bold text-cornflowerblue">
              {t("languages.youwo")}
            </span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {/* Features Dropdown - hidden for now */}

          {/* Pricing */}
          <a
            href="#pricing"
            onClick={(event) => handleAnchorClick(event, "pricing")}
            className="whitespace-nowrap px-3 py-2 text-gray-700 hover:text-cornflowerblue transition-colors font-medium no-underline lg:px-4"
          >
            {t("modern.navbar.pricing")}
          </a>

          {/* Download iOS */}
          <a
            href="https://apps.apple.com/app/youwo-ai-agent/id6760689478"
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap px-3 py-2 text-gray-700 hover:text-cornflowerblue transition-colors font-medium no-underline lg:px-4"
          >
            {t("modern.navbar.downloadIos")}
          </a>

          {/* Download Mac */}
          <a
            href="https://downloads.youwo.ai/mac/YouWo-latest.dmg"
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap px-3 py-2 text-gray-700 hover:text-cornflowerblue transition-colors font-medium no-underline lg:px-4"
          >
            {t("modern.navbar.downloadMac")}
          </a>

          {/* Feature demo */}
          <a
            href="#features"
            onClick={(event) => handleAnchorClick(event, "features")}
            className="whitespace-nowrap px-3 py-2 text-gray-700 hover:text-cornflowerblue transition-colors font-medium no-underline lg:px-4"
          >
            {t("modern.navbar.enterprise")}
          </a>
        </div>

        {/* Right side buttons */}
        <div className="flex items-center gap-3">
          {/* Language Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setLanguageOpen(true)}
            onMouseLeave={() => setLanguageOpen(false)}
          >
            <button className="flex whitespace-nowrap border-none bg-transparent px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:text-cornflowerblue items-center gap-1">
              {languageSwitcherOptions.find((option) => option.code === currentLanguage)
                ?.shortLabel ?? "EN"}
              <Icon icon="lucide:chevron-down" className="text-xs" />
            </button>
            <AnimatePresence>
              {languageOpen && (
                <>
                  <div className="absolute top-full left-0 w-full h-2" />
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 pt-2 w-32 z-50"
                  >
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 py-2">
                      {languageSwitcherOptions.map((option) => (
                        <button
                          key={option.code}
                          onClick={() => handleLanguageChange(option.code)}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors bg-white ${
                            currentLanguage === option.code
                              ? "text-cornflowerblue font-medium"
                              : "text-gray-600 hover:text-cornflowerblue"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Login - Desktop only */}
          <a
            href={buildAppLink('/login')}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden whitespace-nowrap px-3 py-2 text-gray-700 hover:text-cornflowerblue transition-colors font-medium no-underline md:block lg:px-4"
          >
            {t("modern.navbar.login")}
          </a>

          {/* CTA Button - Desktop: Try YouWo AI, Mobile: Download iOS */}
          <Button
            as="a"
            href={buildAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            radius="full"
            className="hidden whitespace-nowrap bg-cornflowerblue text-white hover:bg-blue-600 shadow-md no-underline md:flex"
          >
            {t("modern.navbar.getStarted")}
          </Button>
          <Button
            as="a"
            href="https://apps.apple.com/app/youwo-ai-agent/id6760689478"
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            radius="full"
            className="md:hidden bg-cornflowerblue text-white hover:bg-blue-600 shadow-md no-underline"
            startContent={<Icon icon="lucide:apple" className="text-lg" />}
          >
            {t("modern.navbar.downloadIos")}
          </Button>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Icon icon={mobileMenuOpen ? "lucide:x" : "lucide:menu"} className="text-2xl" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mt-4"
          >
            <div className="py-4 space-y-2">
              {/* Features Section */}
              <div className="px-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{t("modern.navbar.features")}</p>
                {featuresItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-2 text-gray-700 hover:text-cornflowerblue no-underline"
                  >
                    <Icon icon={item.icon} className="text-lg text-gray-400" />
                    <span className="text-sm">{t(item.labelKey)}</span>
                  </Link>
                ))}
              </div>

              <div className="my-2" />

              {/* AI Tools Section */}
              <div className="px-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{t("modern.navbar.freeAiTools")}</p>
                {aiToolsItems.slice(0, 5).map((item, index) => (
                  <Link
                    key={index}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 py-2 text-gray-700 hover:text-cornflowerblue no-underline"
                  >
                    <Icon icon={item.icon} className="text-lg text-gray-400" />
                    <span className="text-sm">{t(item.labelKey)}</span>
                  </Link>
                ))}
                <Link
                  to="/tools"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 py-2 text-cornflowerblue no-underline"
                >
                  <Icon icon="lucide:grid" className="text-lg" />
                  <span className="text-sm font-medium">{t("modern.navbar.exploreAllTools")}</span>
                </Link>
              </div>

              <div className="my-2" />

              {/* Other Links */}
              <div className="px-4 space-y-2">
                <a
                  href="#pricing"
                  onClick={(event) => {
                    setMobileMenuOpen(false);
                    handleAnchorClick(event, "pricing");
                  }}
                  className="block py-2 text-gray-700 hover:text-cornflowerblue no-underline font-medium"
                >
                  {t("modern.navbar.pricing")}
                </a>
                <a
                  href={buildAppLink('/login')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block py-2 text-gray-700 hover:text-cornflowerblue no-underline font-medium"
                >
                  {t("modern.navbar.login")}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
